import React, { type Ref, useMemo, useState } from "react";

import { faEdit, faEllipsisH, faFontCase, faPlus, faUndo } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { flatMap, memoize } from "lodash";
import { EditorView } from "prosemirror-view";
import type { Placement } from "tippy.js";

import { Viewport } from "~/common/ViewportContext";
import MeetingFeedbackModalTrigger from "~/feedback/MeetingFeedbackModal/Trigger";
import { ReactComponent as CloseKeyboard } from "~/lib/prose/css/close-keyboard-button.svg";
import { isNative } from "~/lib/utils";
import { useRecordingState } from "~/lib/videoRecording";
import { Button, Tooltip } from "~/ui";
import { IconButton as _IconButton } from "~/ui/legacyFellowIndex";
import { Menu } from "~/ui/menu";

import type { MenuEntry } from "../extension";
import { getContext } from "./context";
import Toolbar, { groupEntries, isActive, isDisabled, SaveState, ToolbarItem, VerticalRule } from "./toolbar";

import styles from "~/lib/prose/css/toolbar.module.css";

interface MobileToolbarProps {
	closeKeyboard: () => void;
	saveState?: NoteSaveStatus;
	entries: readonly MenuEntry[];
	addBlock: (e: MenuEntry) => void;
	editorView: EditorView;
	extension: Toolbar;
	onMeetingFeedbackClick?: () => void;
	meetingFeedbackSent: boolean;
	meetingFeedbackResponseCount: number;
	lastUpdatedDate: string;
	undo: () => void;
}

type NoteSaveStatus = "saved" | "saving" | "offline";

const IconButton = React.forwardRef(function StyledIconButton(
	props: React.ComponentProps<typeof _IconButton>,
	ref: Ref<HTMLButtonElement>,
) {
	return <_IconButton className={styles.iconButton} {...props} ref={ref} />;
});

function MobileBackButton({ onClick }: { onClick: () => void }) {
	return <IconButton className={styles.mobileBackButton} icon={faChevronDown} onClick={onClick} />;
}

function BlockTypeList({
	isMobile,
	sections,
	addBlock: _addBlock,
}: {
	isMobile: boolean;
	sections: ReturnType<typeof groupEntries>;
	addBlock: (entry: MenuEntry) => void;
}) {
	const addBlock = React.useMemo(() => memoize((entry: MenuEntry) => () => _addBlock(entry)), [_addBlock]);
	const isRecording = useRecordingState();

	return (
		<>
			{sections.types.map(([name, entries], i) => {
				const filteredEntries = entries
					.filter(e => {
						return (
							e.showInToolbar !== false &&
							// Filter out entries that are not supposed to be shown on certain screen sizes,
							!(
								(Viewport.isExtraSmallScreen() && e.hideOnExtraSmallScreen && !isMobile) ||
								(Viewport.isMobile() && e.hideOnMediumScreen && !isMobile)
							)
						);
					})
					.map((entry, entriesIndex) => {
						const tooltipMessage =
							isRecording && entry.title === "Custom video"
								? "Recording in progress..."
								: entry.tooltip || `Add ${entry.title.toLowerCase()}`;

						const placement: Placement = Viewport.isIPhoneSE() && i === 0 && entriesIndex < 2 ? "top-end" : "top";

						return (
							entry.icon &&
							entry.title && (
								<Tooltip disabled={isMobile} content={tooltipMessage} key={entry.title} placement={placement}>
									<IconButton
										onClick={addBlock(entry)}
										icon={entry.icon}
										disabled={entry.title === "Custom video" && isRecording}
										style={{ opacity: isRecording && entry.title === "Custom video" ? 0.5 : 1 }}
									/>
								</Tooltip>
							)
						);
					});

				if (filteredEntries.length === 0) return;

				return (
					<React.Fragment key={name}>
						{i > 0 && <VerticalRule />}
						{filteredEntries}
					</React.Fragment>
				);
			})}
		</>
	);
}

interface FormattingToolbarProps {
	editorView: EditorView;
	extension: Toolbar;
	entries: readonly MenuEntry[];
	className?: string;
}

function FormattingToolbar({ entries, extension, editorView, className }: FormattingToolbarProps) {
	const { selection, doc } = editorView.state;

	let filteredEntries = entries;

	filteredEntries = entries.filter(entry => entry.group !== "insert");

	const sections = groupEntries(filteredEntries);
	const currentBlockTypeEntry = flatMap(sections.types, ([, entries]) => entries).find(
		e => e.active?.(editorView.state) ?? isActive(e, extension, editorView.state),
	);

	let hasLinkedBlockInSelection = false;
	let hasImageStripInSelection =
		selection.$from.node(1)?.type.name === "image_strip" || selection.$to.node(1)?.type.name === "image_strip";
	doc.slice(selection.from, selection.to).content.descendants(node => {
		if (!hasImageStripInSelection && node.type.name.startsWith("image_strip")) {
			hasImageStripInSelection = true;
		}
		if (!hasLinkedBlockInSelection && node.type.name === "todo" && node.attrs.originalBlockId)
			hasLinkedBlockInSelection = true;
		return false;
	});

	const renderItems = React.useCallback(
		(group: [string, MenuEntry[]][], isRadixMenu: boolean) => {
			return group.map(([name, entries]) =>
				entries.map(e => (
					<ToolbarItem
						key={name + e.title}
						asMenuItem={isRadixMenu}
						isMobile
						entry={e}
						extension={extension}
						editorView={editorView}
						active={isActive(e, extension, editorView.state)}
						disabled={(e.greyIfDryRunFalse ?? false) && isDisabled(e, extension, editorView)}
					/>
				)),
			);
		},
		[extension, editorView],
	);

	const renderGroup = React.useCallback(
		(group: [string, MenuEntry[]][], includeSeparator = true) => {
			if (!group || group.length === 0) return;

			return (
				<>
					<div className={styles.group}>{renderItems(group, false)}</div>
					{includeSeparator && <VerticalRule />}
				</>
			);
		},
		[renderItems],
	);

	const renderMenuGroup = React.useCallback(
		(
			group: [string, MenuEntry[]][],
			numberOfItemsToShowInBar?: number,
			icon?: IconDefinition,
			includeSeparator = true,
		) => {
			if (!group || group.length === 0) return;

			// If numberOfItemsToShowInBar is specified, we render the first n items in the bar, and the rest in a menu
			const itemsToShow = group[0][1].splice(0, numberOfItemsToShowInBar ?? 0);
			return (
				<>
					<div className={cx(styles.group)}>
						{!!numberOfItemsToShowInBar && renderItems([[group[0][0], itemsToShow]], false)}
						<Menu>
							<Menu.Trigger asChild={false} type="button" className={styles.item}>
								{icon ? (
									<>
										<FontAwesomeIcon icon={icon} fixedWidth className={styles.icon} />
										<FontAwesomeIcon icon={faCaretDown} className={styles.caret} />
									</>
								) : (
									<FontAwesomeIcon icon={faEllipsisH} fixedWidth />
								)}
							</Menu.Trigger>
							<Menu.Content>
								<Menu.Item>{renderItems(group, true)}</Menu.Item>
							</Menu.Content>
						</Menu>
					</div>
					{includeSeparator && <VerticalRule />}
				</>
			);
		},
		[renderItems],
	);

	const formattingToolbarGroups = React.useMemo(() => {
		const { mostUsedAndSocial, formatting, colouring, indent, reaction } = sections;

		return (
			<>
				{renderGroup([...(mostUsedAndSocial || []), ...(reaction || [])])}
				{renderMenuGroup(formatting, 2)}
				{renderGroup(colouring)}
				{renderGroup(indent, false)}
			</>
		);
	}, [sections, renderGroup, renderMenuGroup]);

	return (
		<div className={cx(styles.menu, className, styles.mobileFormattingToolbar)}>
			{!hasLinkedBlockInSelection && sections.types.reduce((n, [, { length }]) => n + length, 0) > 1 && (
				<>
					<div className={styles.group}>
						<Menu>
							<Menu.Trigger
								type="button"
								className={styles.item}
								disabled={hasLinkedBlockInSelection || hasImageStripInSelection}
								asChild={false}
							>
								{currentBlockTypeEntry ? (
									<>
										{currentBlockTypeEntry.icon && (
											<FontAwesomeIcon icon={currentBlockTypeEntry.icon} fixedWidth className={styles.icon} />
										)}
									</>
								) : (
									<FontAwesomeIcon icon={faEdit} fixedWidth className={styles.icon} />
								)}
								<FontAwesomeIcon icon={faCaretDown} className={styles.caret} />
							</Menu.Trigger>
							<Menu.Content>
								<Menu.Item className={styles.mobile}>
									{sections.types.map(([name, entries]) => (
										<React.Fragment key={name}>
											{entries.map(e => (
												<ToolbarItem
													key={e.title}
													asMenuItem
													isMobile
													entry={e}
													extension={extension}
													editorView={editorView}
													active={isActive(e, extension, editorView.state)}
													disabled={(e.greyIfDryRunFalse ?? false) && isDisabled(e, extension, editorView)}
												/>
											))}
										</React.Fragment>
									))}
								</Menu.Item>
							</Menu.Content>
						</Menu>
					</div>
					<VerticalRule />
				</>
			)}
			{formattingToolbarGroups}
		</div>
	);
}

export function MobileToolbar({
	extension,
	editorView,
	closeKeyboard,
	saveState,
	entries,
	addBlock,
	onMeetingFeedbackClick,
	meetingFeedbackSent,
	meetingFeedbackResponseCount,
	lastUpdatedDate,
	undo,
}: MobileToolbarProps) {
	type ToolbarState = "home" | "add" | "format";
	const [state, _setState] = useState<ToolbarState>("home");
	const makeSetState = useMemo(
		() =>
			memoize((name: ToolbarState) => () => {
				editorView.focus();
				_setState(name);
			}),
		[editorView, _setState],
	);

	const proseContext = getContext(editorView.state);

	return state === "home" ? (
		<div className={cx(styles.mobileMenu, styles.mobileMenuHome)}>
			<div className={cx(styles.mobileMenuGroup, { [styles.reducedPaddings]: onMeetingFeedbackClick })}>
				<IconButton icon={faPlus} onClick={makeSetState("add")} />
				<IconButton icon={faFontCase} onClick={makeSetState("format")} />
				<IconButton icon={faUndo} onClick={undo} />
			</div>
			<div className={cx(styles.mobileMenuGroup, { [styles.reducedPaddings]: onMeetingFeedbackClick })}>
				{onMeetingFeedbackClick && (
					<MeetingFeedbackModalTrigger
						onClick={onMeetingFeedbackClick}
						feedbackSent={meetingFeedbackSent}
						feedbackCount={meetingFeedbackResponseCount}
					/>
				)}
				{isNative && (
					<Button size="wide" className={styles.closeKeyboardButton} onClick={closeKeyboard}>
						<CloseKeyboard />
					</Button>
				)}
				{saveState && (
					<SaveState
						state={saveState}
						showOfflineTooltip
						hasAccessToVersioning={proseContext?.hasAccessToVersioning ?? false}
						lastUpdatedDate={lastUpdatedDate}
					/>
				)}
			</div>
		</div>
	) : state === "add" ? (
		<div className={styles.mobileMenu}>
			<div className={cx(styles.mobileMenuGroup, styles.mobileMenuBackGroup)}>
				<MobileBackButton onClick={makeSetState("home")} />
				<div className={styles.mobileMenuFade} />
			</div>
			<div className={cx(styles.mobileMenuGroup, styles.mobileMenuTypeList)}>
				<BlockTypeList isMobile sections={groupEntries(entries)} addBlock={addBlock} />
			</div>
		</div>
	) : (
		<div className={styles.mobileMenu}>
			<div className={cx(styles.mobileMenuGroup, styles.mobileMenuBackGroup)}>
				<MobileBackButton onClick={makeSetState("home")} />
				<div className={styles.mobileMenuFade} />
			</div>
			<FormattingToolbar
				entries={entries}
				extension={extension}
				editorView={editorView}
				className={cx(styles.mobileMenuGroup, styles.mobileMenuTypeList)}
			/>
		</div>
	);
}
